'use client';

import { useState, useEffect } from 'react';
import { useLocale } from 'next-intl';
import { useAuth } from '@/components/AuthProvider';

/** localStorage keys to preserve when clearing public data */
const PROTECTED_KEYS = [
  'auth_token',
  'theme',
  'theme-mode',
];

export function AdminSheet() {
  const { isElevated, user } = useAuth();
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [cleared, setCleared] = useState(false);

  // Don't render anything for non-admins
  if (!isElevated) return null;

  const handleClearStorage = () => {
    const preserved: Record<string, string> = {};
    for (const key of PROTECTED_KEYS) {
      const val = localStorage.getItem(key);
      if (val != null) preserved[key] = val;
    }
    localStorage.clear();
    for (const [key, val] of Object.entries(preserved)) {
      localStorage.setItem(key, val);
    }
    sessionStorage.clear();
    setCleared(true);
    setTimeout(() => setCleared(false), 2000);
  };

  const handleGoAdmin = () => {
    window.location.href = `/${locale}/admin`;
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <>
      {/* Trigger — small shield icon in header area */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center justify-center cursor-pointer transition-colors duration-200"
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          border: '1px solid rgba(234,179,8,0.25)',
          background: 'rgba(234,179,8,0.08)',
          color: 'rgb(234,179,8)',
        }}
        aria-label="Admin tools"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-[300]"
          style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setOpen(false)}
        />
      )}

      {/* Slide-up sheet */}
      <div
        className="fixed left-0 right-0 z-[301] flex justify-center"
        style={{
          bottom: 0,
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 430,
            background: 'var(--pub-surface-2)',
            borderTop: '1px solid var(--pub-border)',
            borderRadius: '20px 20px 0 0',
            padding: '0 20px 32px',
          }}
        >
          {/* Handle */}
          <div className="flex justify-center" style={{ padding: '10px 0 16px' }}>
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: 'var(--pub-border)',
              }}
            />
          </div>

          {/* Header */}
          <div className="flex items-center gap-2.5" style={{ marginBottom: 18 }}>
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'rgba(234,179,8,0.1)',
                border: '1px solid rgba(234,179,8,0.2)',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="rgb(234,179,8)" strokeWidth="2" width="16" height="16">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div>
              <div
                style={{
                  fontFamily: 'var(--font-pub-display)',
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--pub-text-primary)',
                  letterSpacing: '-0.02em',
                }}
              >
                Admin Tools
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-pub-mono)',
                  fontSize: 10,
                  color: 'var(--pub-text-muted)',
                  letterSpacing: '0.04em',
                  marginTop: 1,
                }}
              >
                {user?.name || user?.employeeId || 'operator'}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <SheetButton
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              }
              label="Admin Panel"
              sub={`/${locale}/admin`}
              onClick={handleGoAdmin}
            />
            <SheetButton
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              }
              label="Reload Page"
              sub="Hard refresh"
              onClick={handleReload}
            />
            <SheetButton
              icon={
                cleared ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-green)" strokeWidth="2.5" width="16" height="16">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  </svg>
                )
              }
              label={cleared ? 'Cleared!' : 'Clear Local Data'}
              sub="Vault, migrations, session — keeps auth"
              onClick={handleClearStorage}
              variant={cleared ? 'success' : 'danger'}
            />
          </div>

          {/* Environment info */}
          <div
            style={{
              marginTop: 14,
              padding: '10px 14px',
              borderRadius: 12,
              background: 'var(--pub-surface-3)',
              border: '1px solid var(--pub-border)',
            }}
          >
            <div style={{
              fontFamily: 'var(--font-pub-mono)',
              fontSize: 10,
              color: 'var(--pub-text-muted)',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}>
              Environment
            </div>
            <div className="flex items-center gap-2">
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: process.env.NODE_ENV === 'production' ? 'var(--pub-green)' : 'rgb(234,179,8)',
                  boxShadow: process.env.NODE_ENV === 'production'
                    ? '0 0 6px var(--pub-green-glow)'
                    : '0 0 6px rgba(234,179,8,0.4)',
                }}
              />
              <span style={{
                fontFamily: 'var(--font-pub-mono)',
                fontSize: 11,
                fontWeight: 500,
                color: 'var(--pub-text-primary)',
                letterSpacing: '0.03em',
              }}>
                {process.env.NODE_ENV}
              </span>
            </div>
          </div>

          {/* Close */}
          <button
            onClick={() => setOpen(false)}
            className="w-full transition-colors duration-150"
            style={{
              marginTop: 14,
              padding: '11px 0',
              borderRadius: 12,
              border: '1px solid var(--pub-border)',
              background: 'var(--pub-surface-3)',
              fontFamily: 'var(--font-pub-mono)',
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.03em',
              color: 'var(--pub-text-muted)',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

function SheetButton({
  icon,
  label,
  sub,
  onClick,
  variant = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'success';
}) {
  const colors = {
    default: { color: 'var(--pub-text-secondary)', bg: 'var(--pub-surface-3)', border: 'var(--pub-border)' },
    danger: { color: 'rgb(239,68,68)', bg: 'rgba(239,68,68,0.06)', border: 'rgba(239,68,68,0.15)' },
    success: { color: 'var(--pub-green)', bg: 'var(--pub-green-dim)', border: 'rgba(34,197,94,0.2)' },
  }[variant];

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 text-left transition-colors duration-150"
      style={{
        padding: '12px 14px',
        borderRadius: 14,
        border: `1px solid ${colors.border}`,
        background: colors.bg,
        cursor: 'pointer',
      }}
    >
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{ color: colors.color }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: colors.color === 'var(--pub-text-secondary)' ? 'var(--pub-text-primary)' : colors.color,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 10,
            color: 'var(--pub-text-muted)',
            marginTop: 1,
            letterSpacing: '0.03em',
          }}
        >
          {sub}
        </div>
      </div>
      <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-muted)" strokeWidth="2" width="14" height="14" style={{ flexShrink: 0 }}>
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}
